import { Navigate, useNavigate, useParams } from 'react-router-dom'

import {
  FBPageInnerTabsContainer,
  FBPageTableContainer,
} from 'components/FbUI/Page/styles'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { SequencingSettings } from 'features/settings/components/SequencingSettings'
import { SenderEmailSettings } from 'features/settings/components/SenderEmailSettings'
import { CompanyDomainsTable } from 'features/settings/components/CompanyDomainsTable'
import { useAuth } from 'context/authentication/useAuth'
import { useMemo } from 'react'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

type OutreachTab = 'sequencing' | 'email-outreach' | 'domains'

const DEFAULT_TAB: OutreachTab = 'sequencing'

export function OutreachSettingsPage() {
  const params = useParams()
  const navigate = useNavigate()
  const tab = (params.tab as OutreachTab) ?? DEFAULT_TAB
  const { isAllowed } = useAuth()
  const isReadOnly = !isAllowed('Admin')
  const featureFlags = useFeatureFlagService()

  const tabs = useMemo(() => {
    const allTabs: { key: OutreachTab; title: string }[] = [
      { key: 'sequencing', title: 'Sequencing' },
      { key: 'email-outreach', title: 'Email Outreach' },
    ]

    if (featureFlags.enableCompanyEmailExtensionSettings) {
      allTabs.push({ key: 'domains', title: 'Domains' })
    }

    return allTabs
  }, [featureFlags.enableCompanyEmailExtensionSettings])

  const validTabs = useMemo(() => new Set(tabs.map((t) => t.key)), [tabs])

  if (params.tab && !validTabs.has(params.tab as OutreachTab)) {
    return <Navigate to={`/settings/outreach/${DEFAULT_TAB}`} replace />
  }

  return (
    <div className="w-full h-full flex flex-col flex-1 min-h-0 bg-surface-page">
      <FBPageInnerTabsContainer>
        <UnderlineTabs<OutreachTab>
          tabs={tabs}
          activeKey={tab}
          onChange={(value) => navigate(`/settings/outreach/${value}`)}
          idPrefix="outreach-settings"
          aria-label="Outreach settings tabs"
        />
      </FBPageInnerTabsContainer>
      {tab === 'sequencing' && (
        <FBPageTableContainer className="bg-surface-page">
          <SequencingSettings />
        </FBPageTableContainer>
      )}
      {tab === 'email-outreach' && (
        <FBPageTableContainer className="bg-surface-page">
          <SenderEmailSettings />
        </FBPageTableContainer>
      )}
      {tab === 'domains' && (
        <FBPageTableContainer className="bg-surface-page">
          <CompanyDomainsTable disabled={isReadOnly} />
        </FBPageTableContainer>
      )}
    </div>
  )
}
